import type { ChtLimits, CylinderReading } from '../types/engine';
import { classifyCht } from '../lib/readings';

type Metric = 'EGT' | 'CHT';

type CylinderStripProps = {
  cyls: CylinderReading[];
  metric?: Metric;
  chtLimits?: ChtLimits;
};

function metricValue(c: CylinderReading, metric: Metric): number | undefined {
  return metric === 'EGT' ? c.egt : c.cht;
}

export function CylinderStrip({ cyls, metric = 'EGT', chtLimits }: CylinderStripProps) {
  const vals = cyls
    .map((c) => metricValue(c, metric))
    .filter((v): v is number => v !== undefined);

  if (vals.length === 0) {
    return (
      <div
        style={{
          padding: '24px 0',
          textAlign: 'center',
          fontSize: 13,
          color: 'var(--ink-3)',
        }}
      >
        No {metric} values entered yet.
      </div>
    );
  }

  const actualMin = Math.min(...vals);
  const actualMax = Math.max(...vals);
  const pad = metric === 'EGT' ? 80 : 40;
  const min = Math.max(0, Math.floor((actualMin - pad) / 50) * 50);
  const max = Math.ceil((actualMax + pad) / 50) * 50;
  const range = max - min || 1;

  return (
    <div>
      {cyls.map((c) => {
        const v = metricValue(c, metric);
        const pct = v !== undefined ? ((v - min) / range) * 100 : 0;
        const peak =
          metric === 'EGT' && c.peakEgt !== undefined
            ? ((c.peakEgt - min) / range) * 100
            : null;

        let color = 'var(--ink-4)';
        if (v !== undefined) {
          if (metric === 'CHT') {
            const state = classifyCht(v, chtLimits ?? { ok: 380, warn: 400, danger: 420 });
            color =
              state === 'danger'
                ? 'var(--danger)'
                : state === 'warn'
                  ? 'var(--warn)'
                  : 'oklch(0.55 0.15 210)';
          } else {
            color = c.peakedAt !== undefined ? 'var(--peak)' : 'oklch(0.55 0.12 75)';
          }
        }

        return (
          <div key={c.id} className="cyl-row">
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                color: 'var(--ink-3)',
                fontWeight: 600,
              }}
            >
              #{c.id}
            </div>
            <div className="cyl-bar">
              {v !== undefined && (
                <div
                  className="cyl-bar-fill"
                  style={{
                    width: `${Math.max(2, Math.min(100, pct))}%`,
                    background: color,
                  }}
                />
              )}
              {peak !== null && <div className="cyl-bar-peak" style={{ left: `${peak}%` }} />}
            </div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 14,
                fontWeight: 500,
                textAlign: 'right',
                color: v === undefined ? 'var(--ink-4)' : 'var(--ink)',
              }}
            >
              {v !== undefined ? (
                <>
                  {Math.round(v)}
                  <span className="unit">°F</span>
                </>
              ) : (
                '—'
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
